/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */

import AddchartIcon from '@mui/icons-material/Addchart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import LocalPoliceOutlinedIcon from '@mui/icons-material/LocalPoliceOutlined';
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HomeIcon from '@mui/icons-material/Home';
import {
  Badge,
  Divider,
  Icon,
  IconButton,
  List,
  Tooltip,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import { NavLinksProps } from "@propTypes/index";
import { CustomCountIcon, useToastAlert } from "common";
import { AppBarStyle } from "customStyles/index";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { extractPageName } from "../../../common/CustomTable/helpers";
import { AlertColor } from "../../../customEnums/AlertColor";
import { useNavStore, useUserStore } from "../../../state";
import CustomListItem from "./CustomListItem";


const drawerWidth = 256

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: 256,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const NavLinks: React.FC<NavLinksProps> = ({
  handleDrawerOpen, expanded, handleDrawerClose, setOpen }) => {


  const locationObj = useLocation()

  const pageName = extractPageName(locationObj.pathname)

  const [openMenu, setOpenMenu] = useState(false)
  const [openRapbackSubs, setOpenRapbackSubs] = useState(false)

  const userConfig = useUserStore(state => state.userConfig)
  const needsAttention = useNavStore(state => state.needsAttention);
  const rapbackSubscriptionAttentionShown = useNavStore(state => state.rapbackSubscriptionAttentionShown);
  const setRapbackSubscriptionAttentionShown = useNavStore(state => state.setRapbackSubscriptionAttentionShown);
  const { enqueueAlert, dismissAlert } = useToastAlert();
  const featureFlags = useUserStore(state => state.featureFlags);

  const { usersPermissions, userEmail, userClosedRbSub } = userConfig;

  const STATE = process.env.REACT_APP_CONFIG_STATE!;
  const SPECIFIC_STATE = process.env.REACT_APP_STAGE;
  const enableRBSubscriptions = STATE === "ga" || SPECIFIC_STATE === "mcsandbox3"
  const enableRapbackSummary = true;
  const enableBatchPrinting = STATE !== "ga"

  const prenotificationsNum = useNavStore(state => state.preNotifcationsNum)
  const validationsNum = useNavStore(state => state.validationsNum)

  const attentionAlertIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (needsAttention && !rapbackSubscriptionAttentionShown && !userClosedRbSub && usersPermissions["viewRapbackSubscriptions"]) {
      if (!attentionAlertIdRef.current) {
        attentionAlertIdRef.current = enqueueAlert({
          title: "Attention Required",
          message: "Rapback subscriptions require your attention.",
          severity: AlertColor.WARNING,
          onClose: () => {
            attentionAlertIdRef.current = null;
            setRapbackSubscriptionAttentionShown(true);
          }
        });
      }
    } else if (attentionAlertIdRef.current) {
      dismissAlert(attentionAlertIdRef.current);
      attentionAlertIdRef.current = null;
    }
  }, [needsAttention, rapbackSubscriptionAttentionShown, userClosedRbSub, usersPermissions, enqueueAlert, dismissAlert, setRapbackSubscriptionAttentionShown])

  const handleToggleMenu = () => {
    setOpenMenu(!openMenu)
  }

  const handleToggleRapbackSubs = () => {
    setOpenRapbackSubs((!openRapbackSubs))
  }

  useEffect(() => {
    if (expanded) {
      setOpenMenu(true)
      setOpenRapbackSubs(true)
    }
    if (!expanded) {
      setOpenMenu(false)
      setOpenRapbackSubs(false)
    }
  }, [expanded])

  return (
    <Drawer variant="permanent" open={expanded}>
      <DrawerHeader>
        <Tooltip title={expanded ? "Close Drawer" : ""}>
          <IconButton onClick={() => {
            setOpenRapbackSubs(false)
            handleDrawerClose()
          }}
            tabIndex={expanded ? 0 : -1}
            data-testid="close-drawer-icon-button"
            aria-label="Close drawer"
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

      </DrawerHeader>
      <Divider aria-hidden role="separator" />
      <List sx={AppBarStyle.listStyle}>
        {
          featureFlags["dashboardqueue-showhide"] && (
            <CustomListItem
              dataTestId="dashboardLink"
              pageName="dashboard"
              label="Dashboard"
              expanded={expanded}
              setOpen={setOpen}
            >
              <Tooltip title="Dashboard">
                <HomeIcon
                  color={pageName === 'dashboard' ? "primary" : "inherit"}
                  aria-label="Dashboard"
                />
              </Tooltip>
            </CustomListItem>
          )
        }
        <CustomListItem dataTestId="transactionSummaryLink" pageName="transaction-summary" label="Transaction Summary" expanded={expanded} setOpen={setOpen}>
          <Tooltip title="Transaction Summary">
            <SwapHorizIcon color={pageName === 'transaction-summary' ? "primary" : "inherit"} aria-label="Transaction Summary" />
          </Tooltip>
        </CustomListItem>
        {
          (usersPermissions?.['viewRapBack']) && enableRapbackSummary && (
            <CustomListItem dataTestId="rbSummaryLink" lastTopItem={(usersPermissions?.['viewBatchPrinting'] || usersPermissions?.['viewRapbackSubscriptions']) ? false : true} pageName="rapback-summary" label="Rapback Summary" expanded={expanded} setOpen={setOpen}>
              <Tooltip title="Rapback Summary">
                <LocalPoliceOutlinedIcon color={pageName === 'rapback-summary' ? "primary" : "inherit"} aria-label="Rapback Summary" />
              </Tooltip>
            </CustomListItem>
          )
        }
        {/*Last icon of the top half must set lastTopItem true. If adding new icons remove lastTopItem from previous last item and add to newest item*/}
        {
          usersPermissions?.['viewRapbackSubscriptions'] && featureFlags["rapbacksubscription-showhide"] && (
            <CustomListItem
              dataTestId="rbSubLink"
              expandable={true}
              lastTopItem={!enableBatchPrinting && enableRBSubscriptions && !openRapbackSubs}
              handleDrawerOpen={handleDrawerOpen}
              handleToggleMenu={handleToggleRapbackSubs}
              openMenu={openRapbackSubs}
              pageName="rapback-subscriptions"
              label="Rapback Subscriptions"
              expanded={expanded}
              setOpen={setOpen}
            >

              <Badge badgeContent={prenotificationsNum > 0 || validationsNum > 0 ? <PriorityHighOutlinedIcon color="warning" fontSize="inherit" /> : null} sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: 'error.dark',
                  color: 'white',
                  padding: 0
                }
              }}>

                <Tooltip title="Rapback Subscriptions">
                  <BookmarkBorderIcon
                    color={pageName === 'rapback-maintenance' || pageName === 'rapback-prenotifications' && !expanded || pageName === 'rapback-validations' && !openRapbackSubs
                      ? "primary" : "inherit"}
                    aria-label="Rapback-Subscriptions"
                  />
                </Tooltip>
              </Badge>
            </CustomListItem>
          )
        }
        {
          openRapbackSubs && expanded && usersPermissions?.['viewRapbackSubscriptions'] && featureFlags["rapbacksubscription-showhide"] && (
            <CustomListItem dataTestId="rbMaintenanceLink" pageName="rapback-maintenance" label="Maintenance" expanded={expanded} setOpen={setOpen}>
              <Icon></Icon>
            </CustomListItem>

          )
        }
        {
          openRapbackSubs && expanded && usersPermissions?.['viewRapbackSubscriptions'] && featureFlags["rapbacksubscription-showhide"] && (

            <CustomListItem dataTestId="rbPrenotificationsLink" pageName="rapback-prenotifications" label="Pre-notifications" expanded={expanded} setOpen={setOpen}>
              {

                prenotificationsNum > 0
                  ? <CustomCountIcon content={prenotificationsNum} />


                  : <Icon></Icon>
              }
            </CustomListItem>


          )
        }
        {
          openRapbackSubs && expanded && usersPermissions?.['viewRapbackSubscriptions'] && featureFlags["rapbacksubscription-showhide"] && (

            <CustomListItem dataTestId="rbValidationsLink" pageName="rapback-validations" label="Validations" expanded={expanded} setOpen={setOpen} lastTopItem={openRapbackSubs}>
              {

                validationsNum > 0
                  ? <CustomCountIcon content={validationsNum} />


                  : <Icon></Icon>
              }
            </CustomListItem>


          )
        }
        {
          usersPermissions?.['viewBatchPrinting'] && featureFlags["batchprinting-showhide"] && (
            <CustomListItem dataTestId="batchPrintingLink" lastTopItem={true} pageName="batch-printing" label="Batch Printing" expanded={expanded} setOpen={setOpen}>
              <Tooltip title="Batch Printing">
                <AutoAwesomeMotionIcon color={pageName === 'batch-printing' ? "primary" : "inherit"} aria-label="Batch Printing" />
              </Tooltip>
            </CustomListItem>
          )
        }
        {/*Start of bottom half icons*/}
        {
          usersPermissions?.['viewReporting'] && featureFlags["reporting-showhide"] && (
            <CustomListItem dataTestId="reportsLink" pageName="reports" label="Reports" expanded={expanded} setOpen={setOpen} userEmail={userEmail}>
              {
                <Tooltip title="Reports">
                  <AddchartIcon color="action" aria-label="Reports" />
                </Tooltip>
              }
            </CustomListItem>
          )

        }
        {
          usersPermissions?.['viewAdministration'] && (
            <CustomListItem
              dataTestId="adminLink"
              expandable={true}
              handleDrawerOpen={handleDrawerOpen}
              handleToggleMenu={handleToggleMenu}
              openMenu={openMenu}
              pageName="administration"
              label="Administration"
              expanded={expanded}
              setOpen={setOpen}
            >
              <Tooltip title="Admin">
                <SettingsIcon
                  color={pageName === 'agency-management' || pageName === 'user-management' && !expanded || pageName === 'agency-management' || pageName === 'user-management' && !openMenu
                    ? "primary" : "inherit"}
                  aria-label="Administration"
                />
              </Tooltip>
            </CustomListItem>)
        }
        {
          openMenu && expanded && usersPermissions?.['viewAgencies'] && (
            <CustomListItem dataTestId="agencyLink" pageName="agency-management" label="Agency Management" expanded={expanded} setOpen={setOpen}>
              <Icon></Icon>
            </CustomListItem>
          )
        }
        {
          openMenu && expanded && usersPermissions?.['viewUserManagement'] && featureFlags["usermng-showhide"] && (
            <CustomListItem dataTestId="userManagementLink" pageName="user-management" label="User Management" expanded={expanded} setOpen={setOpen}>
              <Icon></Icon>
            </CustomListItem>
          )
        }
      </List>
    </Drawer>
  );
};

export default NavLinks;
